import {
  LASER_FADE_DURATION,
  LASER_WHITE_CORE_RATIO,
  LASER_CORE_COLOR
} from 'constants/laserPen';

/**
 * 雷射筆的純繪製邏輯。
 * 刻意與 React 元件分開：這裡不碰 state、不碰 DOM 以外的東西，
 * 只吃 2D context 與軌跡資料，方便單獨驗證。
 */

/**
 * 一筆軌跡此刻的不透明度。
 * 還沒放開就是全不透明；放開後依各自的 releasedAt 線性淡出，
 * 因此多筆軌跡各自計時、互不影響。
 */
export const strokeAlpha = (stroke, now) => {
  if (stroke.releasedAt === null) return 1;
  const elapsed = now - stroke.releasedAt;
  if (elapsed >= LASER_FADE_DURATION) return 0;
  return 1 - elapsed / LASER_FADE_DURATION;
};

/** 淡出是否已完成（完成的軌跡直接從陣列移除，不留任何殘影）。 */
export const isStrokeExpired = (stroke, now) =>
  stroke.releasedAt !== null && now - stroke.releasedAt >= LASER_FADE_DURATION;

/**
 * 把一串點描成平滑路徑：以相鄰兩點的中點為節點畫二次貝茲、
 * 控制點取原始點，轉折處因此不會出現尖角。
 */
export const tracePath = (ctx, points) => {
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);

  if (points.length === 2) {
    ctx.lineTo(points[1].x, points[1].y);
    return;
  }

  for (let i = 1; i < points.length - 1; i++) {
    const midX = (points[i].x + points[i + 1].x) / 2;
    const midY = (points[i].y + points[i + 1].y) / 2;
    ctx.quadraticCurveTo(points[i].x, points[i].y, midX, midY);
  }

  // 補上最後一段，否則筆畫會比實際短一截
  const last = points[points.length - 1];
  ctx.lineTo(last.x, last.y);
};

/**
 * 一筆兩描做出「中央白色＋左右彩色邊線」：
 * 先用完整線寬描彩色，再用較細的線寬把白芯描在同一條中心線上。
 * 因為兩描共用同一條路徑，白芯自然置中，
 * 兩側各露出 (width - width * LASER_WHITE_CORE_RATIO) / 2 的彩色邊。
 * 白芯顏色固定，所以切換顏色只會改變邊線。
 */
export const drawLaserStroke = (ctx, stroke) => {
  const { points, color, width } = stroke;
  if (!points || points.length === 0) return;

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // 只點一下沒有拖曳時畫成圓點，否則 stroke() 什麼都不會出現
  if (points.length < 2) {
    const { x, y } = points[0];

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, width / 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = LASER_CORE_COLOR;
    ctx.beginPath();
    ctx.arc(x, y, (width * LASER_WHITE_CORE_RATIO) / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  tracePath(ctx, points);
  ctx.stroke();

  ctx.strokeStyle = LASER_CORE_COLOR;
  ctx.lineWidth = width * LASER_WHITE_CORE_RATIO;
  tracePath(ctx, points);
  ctx.stroke();
};
